/**
 * Floating "Add to cart" button on the mobile product page
 * (markup: functions-floating-atc.php).
 *
 * Shown only once the real add-to-cart form has been scrolled past. Simple products
 * click the real button - never form.submit(), which would skip the delegated
 * YITH WAPO validation and the loader (see add-to-cart-loading.js). Variable
 * products scroll to the variation choice.
 *
 * The button sits next to the Smartsupp launcher: it ends where the launcher
 * starts and is centred on it vertically. The launcher loads
 * asynchronously, so it is measured once it appears and whenever its style
 * changes.
 * Hiding while a panel is open is pure CSS (classes on html/body).
 */

const MOBILE_QUERY = "(max-width: 767px)";
const CHAT_SELECTOR = 'div[data-testid="widgetButtonFrame"]';
const VISIBLE_CLASS = "has-floating-atc";

const CHAT_GAP = 12;

const BUTTON_HEIGHT = 48;

const LAUNCHER_MAX_WIDTH = 120;

const HEADER_OFFSET = 64;
const SCROLL_MARGIN = 16;

const measureChat = (button) => {
  const chat = document.querySelector(CHAT_SELECTOR);

  if (!chat) {
    return null;
  }

  const css = getComputedStyle(chat);
  const right = parseFloat(css.right);
  const bottom = parseFloat(css.bottom);
  const width = parseFloat(css.width);
  const height = parseFloat(css.height);

  if (
    css.display === "none" ||
    [right, bottom, width, height].some(Number.isNaN) ||
    width === 0 ||
    width > LAUNCHER_MAX_WIDTH
  ) {
    return chat;
  }

  const style = button.style;
  style.setProperty("--fatc-right", `${right + width + CHAT_GAP}px`);
  style.setProperty(
    "--fatc-bottom",
    `${bottom + (height - BUTTON_HEIGHT) / 2}px`
  );

  return chat;
};

const watchChat = (button) => {
  const followChat = (chat) => {
    new MutationObserver(() => measureChat(button)).observe(chat, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
  };

  const chat = measureChat(button);

  if (chat) {
    followChat(chat);
    return;
  }

  const observer = new MutationObserver(() => {
    const found = measureChat(button);

    if (found) {
      observer.disconnect();
      followChat(found);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

const scrollToVariations = (form) => {
  const target = form.querySelector("table.variations, .variations") || form;
  const top =
    target.getBoundingClientRect().top +
    window.scrollY -
    HEADER_OFFSET -
    SCROLL_MARGIN;

  window.scrollTo({ top, behavior: "smooth" });
};

export const setupFloatingAddToCart = () => {
  const button = document.querySelector(".floating-atc");
  const form = document.querySelector(".product-content form.cart");
  const realButton = form?.querySelector(".single_add_to_cart_button");

  if (!button || !form || !realButton) {
    return;
  }

  const isVariable = button.dataset.type === "variable";
  const mobile = window.matchMedia(MOBILE_QUERY);
  let scrolledPast = false;

  const update = () => {
    const show = mobile.matches && scrolledPast;

    button.classList.toggle("is-visible", show);
    button.setAttribute("aria-hidden", show ? "false" : "true");
    button.tabIndex = show ? 0 : -1;
    document.documentElement.classList.toggle(VISIBLE_CLASS, show);
  };

  new IntersectionObserver(
    ([entry]) => {
      scrolledPast =
        !entry.isIntersecting && entry.boundingClientRect.top < HEADER_OFFSET;
      update();
    },
    { rootMargin: `-${HEADER_OFFSET}px 0px 0px 0px` }
  ).observe(isVariable ? form : realButton);

  mobile.addEventListener("change", update);

  button.addEventListener("click", () => {
    if (isVariable) {
      scrollToVariations(form);
    } else {
      realButton.click();
    }
  });

  button.hidden = false;
  update();

  watchChat(button);
};
